import { useContext, useCallback, createContext } from "react";
import { StoreApi, useStore } from "zustand";
import { UploadProviderInfo, UploadProviderActions } from "./UploadProvider";
import { UploadSession } from "./utils";

export const UploadProviderContext = createContext<StoreApi<UploadProviderInfo & UploadProviderActions> | null>(null);

export function useUpload<U>(selector: (store: UploadProviderInfo & UploadProviderActions) => U): U {
  const context = useContext(UploadProviderContext);
  if (!context) {
    throw new Error('useUpload must be used within a UploadProvider');
  }
  return useStore(context, selector);
}

export const useUploadFiles = () => {
  return useUpload(s => s.uploadFiles);
};

export const useRetryUploadSessions = () => {
  return useUpload(s => s.retryUploadSessions);
};

export const useIdentifyInterruptedUploads = () => {
  return useUpload(s => s.identifyInterruptedUploads);
};

export const useClearCompletedSessions = () => {
  return useUpload(s => s.clearCompletedSessions);
};

export function useUploadSessions<S extends UploadSession>() {
  const sessions = useUpload(s => s.sessions);
  return sessions as S[];
}

export function useLastUpload() {
  return useUpload(s => s.uploadStarted);
}

export function useUploadSession<S extends UploadSession>(id: string) {
  const selector = useCallback(
    (s: UploadProviderInfo & UploadProviderActions) => {
      return [
        s.sessions.find(s => s.id === id),
        () => s.hideUploadSession(id),
        () => s.cancelUploadSession(id),
        (file: File) => s.retryUploadSession(id, file)
      ] as const;
    },
    [id]
  );
  const [session, hide, cancel, retry] = useUpload(selector);
  if (!session) throw new Error(`No upload session found with id ${id}`);
  return [session as S, hide, cancel, retry] as const;
}
